import { useEffect, useState, type FormEvent, type KeyboardEvent } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { Icon } from "../app/Icon";
import { initials } from "../profiles/ProfileMenu";
import { viewProfile } from "../lib/connections";
import { sendMessage } from "../lib/messaging";

/** Compose a new message inside the two-pane messenger. On first send it creates
 *  the conversation and navigates into the real thread. */
export function NewConversation() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (profileId) viewProfile(profileId).then((d) => setName(d.profile.display_name ?? "member")).catch(() => undefined);
  }, [profileId]);

  async function onSend(e: FormEvent) {
    e.preventDefault();
    if (!profileId || !body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const { conversation_id } = await sendMessage({ profile_id: profileId, body });
      navigate(`/messages/${conversation_id}`);
    } catch {
      setError("Could not send the message.");
      setBusy(false);
    }
  }
  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void onSend(e as unknown as FormEvent);
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-3 border-b border-line px-4 py-3">
        <Link to="/messages" className="text-muted hover:text-ink lg:hidden" aria-label="Back to conversations">
          <Icon name="chevron" className="h-5 w-5 rotate-90" />
        </Link>
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-accent/15 font-display text-xs font-black text-accent-strong">{initials(name || "?")}</span>
        <span className="truncate font-display text-base font-black tracking-tight">{name || "New message"}</span>
      </div>

      <div className="grid min-h-0 flex-1 place-items-center p-6 text-center">
        <p className="font-mono text-sm text-muted">Start the conversation with {name || "this member"}.</p>
      </div>

      <form className="border-t border-line p-3" onSubmit={onSend}>
        {error && <p className="mb-1 px-1 font-mono text-xs text-danger">{error}</p>}
        <div className="flex items-end gap-2">
          <textarea
            className="max-h-32 min-h-[2.5rem] flex-1 resize-none rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink"
            rows={1}
            placeholder="Type a message…"
            aria-label="Message"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={onKey}
            maxLength={2000}
          />
          <Button type="submit" disabled={busy || !body.trim()}>Send</Button>
        </div>
        <p className="mt-1.5 px-1 font-mono text-[10px] text-muted">Press Enter to send · Shift + Enter for new line</p>
      </form>
    </div>
  );
}
